
import { useState, useRef } from 'react';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { OpsEndpointCard } from '@/components/ops/OpsEndpointCard';
import { TimestampDisplay } from '@/components/ops/TimestampDisplay';
import { EmptyState } from '@/components/ops/EmptyState';
import { LoadingSpinner } from '@/components/ops/LoadingSpinner';
import { OpsErrorBoundary } from '@/components/ops/OpsErrorBoundary';
import { History, Search } from 'lucide-react';

const API_BASE = import.meta.env.VITE_RAG_API_URL;

interface ConfigSnapshot {
    status: 'active' | 'archived' | 'snapshot';
    source: string;
    threshold_policy: string;
    semantic_threshold: number;
    cosine_threshold: number;
    min_chunks: number;
    rerank_enabled: boolean;
    rerank_fallback: string;
    snapshot_time: string;
}

export function ConfigHistoryPage() {
    const { session } = useUser();
    const [timestamp, setTimestamp] = useState<string>(new Date().toISOString());
    const [data, setData] = useState<ConfigSnapshot | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isMounted = useRef(true);

    const handleFetch = async () => {
        if (!session?.access_token) return;

        // Basic ISO validation
        if (!timestamp.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            setError("Invalid format. Use ISO 8601 (e.g. 2026-01-01T12:00:00Z)");
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setData(null);

            const res = await axios.get(`${API_BASE}/ops/retrieval/config_at_time`, {
                params: { timestamp },
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: 5000
            });

            if (isMounted.current) {
                setData(res.data);
            }
        } catch (err: any) {
            if (isMounted.current) {
                if (err.response?.status === 404) {
                    setError("No configuration found for this timestamp.");
                } else if (err.response?.status === 401) {
                    setError("Unauthorized");
                } else {
                    setError(err.message || "Failed to fetch configuration history");
                }
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    if (!session) return <EmptyState title="Authentication Required" description="Please sign in to view config history." />;

    return (
        <OpsErrorBoundary>
            <div className="h-[calc(100vh-12rem)] max-w-4xl mx-auto p-4">
                <OpsEndpointCard
                    title="Configuration History"
                    endpoint="/ops/retrieval/config_at_time"
                    lastUpdated={data ? new Date(data.snapshot_time).getTime() : undefined}
                    onRefresh={handleFetch}
                    isLoading={loading}
                    error={error}
                >
                    <div className="space-y-6">
                        {/* Search Input */}
                        <div className="flex gap-4 items-end">
                            <div className="flex-1 space-y-2">
                                <label className="text-sm text-gray-400">Target Timestamp (ISO 8601)</label>
                                <input
                                    type="text"
                                    value={timestamp}
                                    onChange={(e) => setTimestamp(e.target.value)}
                                    className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none"
                                    placeholder="2026-01-01T12:00:00Z"
                                />
                            </div>
                            <button
                                onClick={handleFetch}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                <Search className="w-4 h-4" />
                                Fetch Snapshot
                            </button>
                        </div>

                        {/* Content Area */}
                        {loading ? (
                            <LoadingSpinner fullHeight text="Retrieving historical snapshot..." />
                        ) : data ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-white/10">
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold border-b border-white/10 pb-2 text-white/90">Effective Thresholds</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <DetailItem label="Semantic" value={data.semantic_threshold.toFixed(2)} />
                                        <DetailItem label="Cosine" value={data.cosine_threshold.toFixed(2)} />
                                        <DetailItem label="Min Chunks" value={data.min_chunks} />
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold border-b border-white/10 pb-2 text-white/90">Reranking</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <DetailItem label="Enabled" value={data.rerank_enabled ? "Yes" : "No"} />
                                        <DetailItem label="Fallback" value={data.rerank_fallback} />
                                    </div>
                                </div>

                                <div className="space-y-4 md:col-span-2">
                                    <h3 className="text-lg font-semibold border-b border-white/10 pb-2 text-white/90">Metadata</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <DetailItem label="Source" value={data.source} highlight />
                                        <DetailItem label="Status" value={data.status} />
                                        <DetailItem label="Policy" value={data.threshold_policy} />
                                        <div className="flex flex-col">
                                            <span className="text-muted-foreground text-sm">Snapshot Time</span>
                                            <TimestampDisplay timestamp={data.snapshot_time} showIcon={false} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : !error ? (
                            <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
                                <History className="w-12 h-12 text-gray-600 mb-2" />
                                <h3 className="text-lg font-medium text-white">No Snapshot Loaded</h3>
                                <p className="text-sm text-gray-400 max-w-sm">Enter a timestamp above to view the configuration state at that specific moment.</p>
                            </div>
                        ) : null}
                    </div>
                </OpsEndpointCard>
            </div>
        </OpsErrorBoundary>
    );
}

const DetailItem = ({ label, value, highlight = false }: { label: string, value: string | number, highlight?: boolean }) => (
    <div className="flex flex-col">
        <span className="text-muted-foreground text-sm">{label}</span>
        <span className={`font-mono font-medium ${highlight ? 'text-blue-400' : 'text-gray-200'}`}>{value}</span>
    </div>
);
